import type { ConnectionStatus, MicrovmFleet, OpsSnapshot } from '@/hooks/useOpsSnapshot';
import { currentImageVersion, formatAge, isOnOldImage, type MicrovmFilter } from '@/lib/microvm';

export const PLATFORM_TABS = ['microvms', 'failures', 'capacity', 'lifecycle', 'agent'] as const;
export type PlatformTab = (typeof PLATFORM_TABS)[number];

export function platformTabFrom(value: string | null): PlatformTab {
  return PLATFORM_TABS.find((tab) => tab === value) ?? 'microvms';
}

export function microvmFilterFrom(value: string | null): MicrovmFilter {
  return value === 'orphans' || value === 'outdated' || value === 'all' ? value : 'live';
}

export type PlatformHealthLevel = 'healthy' | 'warning' | 'critical' | 'unknown';
export type PlatformHealthSeverity = 'warning' | 'critical';

export interface PlatformHealthReason {
  id: 'connection' | 'fleet-stale' | 'failures' | 'orphans' | 'old-image' | 'capacity';
  severity: PlatformHealthSeverity;
  text: string;
  /** Where the page shows what this reason is about; the connection has nowhere to go but a reconnect. */
  action?: { label: string; tab: PlatformTab; filter?: MicrovmFilter };
}

export interface PlatformHealth {
  level: PlatformHealthLevel;
  sentence: string;
  reasons: PlatformHealthReason[];
}

const DAY_MS = 24 * 3_600_000;
/** Failed claims in a day at which opening a workspace is broken rather than flaky. */
export const CRITICAL_FAILURES_PER_DAY = 5;
/** Share of an organization's limit at which it is close to not opening another workspace. */
export const NEAR_LIMIT_SHARE = 0.8;

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Failed claims in the 24 hours before `now`. */
export function failuresInLastDay(sandbox: OpsSnapshot['sandbox'], now: number): number {
  return sandbox.recentFailures.filter((failure) => {
    const at = Date.parse(failure.at);
    return Number.isFinite(at) && now - at <= DAY_MS;
  }).length;
}

export interface ImageLag {
  current?: string;
  /** Live VMs on a version other than the current one. */
  outdated: number;
  /** Oldest version a live VM runs, when it differs from the current one. */
  oldest?: string;
  /** Whole versions between the oldest and the current one; undefined when versions are not numbers. */
  versionsBehind?: number;
}

export function imageLag(fleet: Pick<MicrovmFleet, 'image' | 'vms'>): ImageLag {
  const current = currentImageVersion(fleet);
  const outdated = fleet.vms.filter((vm) => isOnOldImage(vm, current));
  if (!current || outdated.length === 0) return { current, outdated: 0 };
  const numeric = outdated.map((vm) => Number(vm.imageVersion)).filter(Number.isFinite);
  const currentNumber = Number(current);
  if (numeric.length !== outdated.length || !Number.isFinite(currentNumber)) {
    return { current, outdated: outdated.length };
  }
  const oldest = Math.min(...numeric);
  return { current, outdated: outdated.length, oldest: String(oldest), versionsBehind: Math.max(0, currentNumber - oldest) };
}

function connectionReason(status: ConnectionStatus): PlatformHealthReason | undefined {
  if (status === 'offline') {
    return {
      id: 'connection',
      severity: 'critical',
      text: 'This page lost its connection to the API, so everything here may be out of date.',
    };
  }
  if (status === 'reconnecting') {
    return {
      id: 'connection',
      severity: 'warning',
      text: 'This page is reconnecting to the API, so the numbers may be a little behind.',
    };
  }
  return undefined;
}

function fleetReasons(fleet: MicrovmFleet, now: number): PlatformHealthReason[] {
  const reasons: PlatformHealthReason[] = [];
  if (fleet.stale) {
    reasons.push({
      id: 'fleet-stale',
      severity: 'warning',
      text: fleet.observedAt
        ? `The worker last checked the MicroVMs ${formatAge(fleet.observedAt, now)} ago, so the counts may be behind.`
        : 'The worker has not checked the MicroVMs yet.',
      action: { label: 'See MicroVMs', tab: 'microvms' },
    });
  }
  const current = currentImageVersion(fleet);
  const orphans = fleet.vms.filter((vm) => !vm.owned);
  const orphanCount = Math.max(fleet.orphans, orphans.length);
  if (orphanCount > 0) {
    const oldOrphans = orphans.filter((vm) => isOnOldImage(vm, current)).length;
    const verb = orphanCount === 1 ? 'has' : 'have';
    let tail = '';
    if (oldOrphans > 0 && orphanCount === 1) tail = ', and it is on an old image';
    else if (oldOrphans === orphanCount) tail = ', and all of them are on an old image';
    else if (oldOrphans > 0) tail = `, and ${oldOrphans} of them ${oldOrphans === 1 ? 'is' : 'are'} on an old image`;
    reasons.push({
      id: 'orphans',
      severity: 'warning',
      text: `${plural(orphanCount, 'MicroVM')} ${verb} no owner${tail}.`,
      action: { label: 'Show MicroVMs with no owner', tab: 'microvms', filter: 'orphans' },
    });
  }
  const lag = imageLag(fleet);
  if (lag.outdated > 0) {
    const behind = lag.versionsBehind ? ` (oldest is ${plural(lag.versionsBehind, 'version')} behind)` : '';
    reasons.push({
      id: 'old-image',
      severity: 'warning',
      text: `${plural(lag.outdated, 'running MicroVM')} ${lag.outdated === 1 ? 'is' : 'are'} not on the current image v${lag.current}${behind}.`,
      action: { label: 'Show MicroVMs on an old image', tab: 'microvms', filter: 'outdated' },
    });
  }
  return reasons;
}

function capacityReason(sandbox: OpsSnapshot['sandbox']): PlatformHealthReason | undefined {
  const limited = sandbox.tenants.filter((tenant) => tenant.limit !== null && tenant.limit > 0);
  const atLimit = limited.filter((tenant) => tenant.active >= tenant.limit!);
  const nearLimit = limited.filter((tenant) => tenant.active < tenant.limit! && tenant.active >= tenant.limit! * NEAR_LIMIT_SHARE);
  if (atLimit.length > 0) {
    const names = atLimit.length === 1 ? atLimit[0]!.organizationName : plural(atLimit.length, 'organization');
    return {
      id: 'capacity',
      severity: 'critical',
      text: `${names} ${atLimit.length === 1 ? 'is' : 'are'} at the workspace limit and cannot open another workspace.`,
      action: { label: 'See capacity', tab: 'capacity' },
    };
  }
  if (nearLimit.length > 0) {
    const names = nearLimit.length === 1 ? nearLimit[0]!.organizationName : plural(nearLimit.length, 'organization');
    return {
      id: 'capacity',
      severity: 'warning',
      text: `${names} ${nearLimit.length === 1 ? 'is' : 'are'} close to the workspace limit.`,
      action: { label: 'See capacity', tab: 'capacity' },
    };
  }
  return undefined;
}

/**
 * One verdict for the Platform page from the ops snapshot it already polls:
 * the connection, claim failures in the last day, MicroVMs with no owner or on
 * an old image, and organizations at or near their workspace limit.
 */
export function platformHealth({ snapshot, status }: { snapshot: OpsSnapshot | null; status: ConnectionStatus }): PlatformHealth {
  if (!snapshot) {
    const reason = connectionReason(status);
    return {
      level: reason ? reason.severity : 'unknown',
      sentence: reason ? 'There is no platform status to show yet because this page cannot reach the API.' : 'Waiting for the first platform status.',
      reasons: reason ? [reason] : [],
    };
  }
  const now = Date.parse(snapshot.generatedAt);
  const reasons: PlatformHealthReason[] = [];
  const connection = connectionReason(status);
  if (connection) reasons.push(connection);

  const failures = failuresInLastDay(snapshot.sandbox, now);
  if (failures > 0) {
    reasons.push({
      id: 'failures',
      severity: failures >= CRITICAL_FAILURES_PER_DAY ? 'critical' : 'warning',
      text: `${plural(failures, 'workspace')} failed to open in the last 24 hours.`,
      action: { label: 'See failures', tab: 'failures' },
    });
  }
  if (snapshot.microvm) reasons.push(...fleetReasons(snapshot.microvm, now));
  const capacity = capacityReason(snapshot.sandbox);
  if (capacity) reasons.push(capacity);

  if (reasons.length === 0) {
    return {
      level: 'healthy',
      sentence: snapshot.microvm
        ? 'No workspace failed to open in the last 24 hours, every MicroVM has an owner and runs the current image, and every organization has room.'
        : 'No workspace failed to open in the last 24 hours and every organization has room.',
      reasons,
    };
  }
  return {
    level: reasons.some((reason) => reason.severity === 'critical') ? 'critical' : 'warning',
    sentence: reasons.map((reason) => reason.text).join(' '),
    reasons,
  };
}

export const PLATFORM_HEALTH_LABELS: Record<PlatformHealthLevel, string> = {
  healthy: 'Healthy',
  warning: 'Needs a look',
  critical: 'Something is wrong',
  unknown: 'Checking',
};
